import { useEffect, useMemo, useState } from "react";
import { Loader2, PackageCheck, RefreshCw, Truck } from "lucide-react";
import type { Period } from "../../funnel-detail/types";
import {
  getFinanceLogisticsOverview,
  type FinanceLogisticsOrder,
  type FinanceLogisticsOverview,
} from "../../../services/financeService";
import { formatCurrency, formatNumber, formatPercentage } from "./utils";

type LogisticsViewId = "total" | "delivered" | "inTransit" | "pdt" | "incident" | "returned";

type LogisticsViewConfig = {
  id: LogisticsViewId;
  label: string;
  title: string;
  description: string;
  statuses: string[];
  color: string;
  valueLabel: string;
};

type LogisticsMetric = LogisticsViewConfig & {
  count: number;
  products: number;
  value: number;
  paid: number;
  percentage: number;
};

const logisticsViews: LogisticsViewConfig[] = [
  {
    id: "total",
    label: "Totales",
    title: "Totales",
    description: "Todos los pedidos capturados desde Dropi en el periodo.",
    statuses: [],
    color: "#14b8a6",
    valueLabel: "Valor de pedidos",
  },
  {
    id: "delivered",
    label: "Finalizadas",
    title: "Finalizadas",
    description: "Pedidos entregados o cerrados correctamente.",
    statuses: ["delivered"],
    color: "#10b981",
    valueLabel: "Valor finalizado",
  },
  {
    id: "inTransit",
    label: "En transito",
    title: "En transito",
    description: "Pedidos con guia activa que siguen en ruta.",
    statuses: ["in_transit"],
    color: "#f59e0b",
    valueLabel: "Valor en transito",
  },
  {
    id: "pdt",
    label: "PDT",
    title: "PDT",
    description: "Pedidos pendientes o con guia creada que aun no avanzan.",
    statuses: ["pending", "guide_generated"],
    color: "#8b5cf6",
    valueLabel: "Valor pendiente",
  },
  {
    id: "incident",
    label: "Novedad",
    title: "En novedad",
    description: "Pedidos con incidencia, error o bloqueo operativo.",
    statuses: ["incident", "error"],
    color: "#ef4444",
    valueLabel: "Valor en novedad",
  },
  {
    id: "returned",
    label: "Devolucion",
    title: "En devolucion",
    description: "Pedidos rechazados o devueltos que golpean caja y margen.",
    statuses: ["returned"],
    color: "#f97316",
    valueLabel: "Valor en devolucion",
  },
];

const orderPaid = (order: FinanceLogisticsOrder) =>
  Number(order.total_paid ?? order.totalPaid ?? order.value ?? 0);

const orderValue = (order: FinanceLogisticsOrder) => Number(order.value ?? 0);

const orderUnits = (order: FinanceLogisticsOrder) => Number(order.units ?? 0);

const inView = (order: FinanceLogisticsOrder, view: LogisticsViewConfig) =>
  view.id === "total" || view.statuses.includes(order.status);

const sumBuckets = (overview: FinanceLogisticsOverview, statuses: string[]) =>
  overview.status_breakdown
    .filter((bucket) => statuses.includes(bucket.id))
    .reduce(
      (acc, bucket) => ({
        count: acc.count + Number(bucket.count ?? 0),
        value: acc.value + Number(bucket.value ?? 0),
      }),
      { count: 0, value: 0 }
    );

export function CompanyLogisticsSection({ period }: { period: Period }) {
  const [overview, setOverview] = useState<FinanceLogisticsOverview | null>(null);
  const [activeView, setActiveView] = useState<LogisticsViewId>("total");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    getFinanceLogisticsOverview(period)
      .then((data) => {
        if (!mounted) return;
        setOverview(data);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [period]);

  const metrics = useMemo<LogisticsMetric[]>(() => {
    if (!overview) return [];

    const totalOrders =
      Number(overview.summary.total_orders ?? 0) ||
      overview.orders.length ||
      overview.status_breakdown.reduce((sum, bucket) => sum + Number(bucket.count ?? 0), 0);

    return logisticsViews.map((view) => {
      const orders = overview.orders.filter((order) => inView(order, view));
      const bucketTotals = view.id === "total"
        ? {
            count: totalOrders,
            value: overview.status_breakdown.reduce((sum, bucket) => sum + Number(bucket.value ?? 0), 0),
          }
        : sumBuckets(overview, view.statuses);

      const count = orders.length > 0 ? orders.length : bucketTotals.count;
      const value = orders.length > 0 ? orders.reduce((sum, order) => sum + orderValue(order), 0) : bucketTotals.value;
      const products = orders.reduce((sum, order) => sum + orderUnits(order), 0);
      const paid = orders.length > 0 ? orders.reduce((sum, order) => sum + orderPaid(order), 0) : value;

      return {
        ...view,
        count,
        products,
        value,
        paid,
        percentage: totalOrders > 0 ? (count / totalOrders) * 100 : 0,
      };
    });
  }, [overview]);

  const selected = metrics.find((item) => item.id === activeView) ?? metrics[0];

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const data = await getFinanceLogisticsOverview(period);
      setOverview(data);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <section
        id="finance-company-logistics-target"
        className="flex min-h-[320px] items-center justify-center rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-500"
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Cargando logística de Dropi...
      </section>
    );
  }

  if (!overview?.dropi_connected) {
    return (
      <section id="finance-company-logistics-target" className="rounded-lg border border-orange-100 bg-white p-6">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
              <Truck className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Logística Dropi</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">
                Conecta Dropi para ver totales, finalizadas, tránsito, PDT, novedades y devoluciones dentro de Logística.
              </p>
            </div>
          </div>
          <span className="w-fit rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700">
            Dropi no conectado
          </span>
        </div>
      </section>
    );
  }

  if (!selected) return null;

  return (
    <section id="finance-company-logistics-target" className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <header className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Logística Dropi</h2>
          <p className="mt-1 text-sm text-gray-500">
            Estado operativo consolidado para {overview.period.label || period.label}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Actualizar
          </button>
        </div>
      </header>

      <div className="grid lg:grid-cols-[minmax(200px,240px)_minmax(0,1fr)]">
        <aside className="border-b border-gray-100 bg-gray-50/70 p-3 lg:border-b-0 lg:border-r">
          <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
            {metrics.map((item) => {
              const active = item.id === selected.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveView(item.id)}
                  className={`flex min-w-[170px] items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition lg:min-w-0 ${
                    active
                      ? "border-[#2b5ce6] bg-white text-gray-950 shadow-sm"
                      : "border-transparent bg-transparent text-gray-500 hover:bg-white hover:text-gray-900"
                  }`}
                >
                  <span>
                    <span className="block text-sm font-bold">{item.label}</span>
                    <span className="mt-0.5 block text-[11px] font-medium text-gray-400">
                      {formatPercentage(item.percentage)}
                    </span>
                  </span>
                  <span className="rounded-full bg-white px-2 py-1 text-xs font-bold text-gray-700 shadow-sm ring-1 ring-gray-100">
                    {formatNumber(item.count)}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <div className="p-5">
          <div className="border-b border-gray-100 pb-4">
            <div className="flex items-center gap-2">
              <PackageCheck className="h-4 w-4" style={{ color: selected.color }} />
              <h3 className="text-sm font-bold text-gray-900">{selected.title}</h3>
            </div>
            <p className="mt-1 text-sm text-gray-500">{selected.description}</p>
          </div>

          <div className="grid items-center gap-6 py-7 [grid-template-columns:repeat(auto-fit,minmax(min(250px,100%),1fr))]">
            <LogisticsDonut metric={selected} />
            <IndicatorPanel metric={selected} currency={overview.summary.currency} />
          </div>
        </div>
      </div>
    </section>
  );
}

function LogisticsDonut({ metric }: { metric: LogisticsMetric }) {
  const degrees = metric.id === "total" ? 360 : Math.max(0, Math.min(360, metric.percentage * 3.6));
  const background =
    metric.id === "total"
      ? `conic-gradient(from 140deg, ${metric.color}, #2b5ce6 62%, #6d4f9f)`
      : `conic-gradient(${metric.color} 0deg ${degrees}deg, #eef2f7 ${degrees}deg 360deg)`;

  return (
    <div className="flex min-w-0 justify-center">
      <div
        className="relative h-56 w-56 rounded-full shadow-inner sm:h-64 sm:w-64 2xl:h-72 2xl:w-72"
        style={{ background }}
        aria-label={`${metric.title}: ${formatPercentage(metric.percentage)}`}
      >
        <div className="absolute inset-[32px] flex flex-col items-center justify-center rounded-full bg-white text-center shadow-[inset_0_0_0_1px_rgba(226,232,240,.9)]">
          <span className="max-w-[150px] text-sm font-semibold leading-5 text-gray-700">{metric.title}</span>
          <span className="mt-2 text-3xl font-black tracking-normal text-gray-950">
            {formatPercentage(metric.id === "total" ? 100 : metric.percentage, metric.percentage >= 10 ? 0 : 1)}
          </span>
        </div>
      </div>
    </div>
  );
}

function IndicatorPanel({ metric, currency }: { metric: LogisticsMetric; currency: string }) {
  const rows = [
    { label: "Pedidos", value: formatNumber(metric.count) },
    { label: "Productos", value: formatNumber(metric.products) },
    { label: metric.valueLabel, value: formatCurrency(metric.value, currency) },
    { label: "Recaudo registrado", value: formatCurrency(metric.paid, currency) },
  ];

  return (
    <div className="min-w-0 rounded-lg bg-gray-100 px-4 py-5 sm:px-5">
      <div className="grid grid-cols-[1fr_auto] gap-4 border-b border-gray-200 pb-3 text-sm font-bold text-gray-500">
        <span>Indicadores</span>
        <span>Cantidad</span>
      </div>
      <div className="divide-y divide-gray-200">
        {rows.map((row) => (
          <div key={row.label} className="grid grid-cols-[1fr_auto] items-center gap-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: metric.color }} />
              <span className="truncate text-sm font-semibold text-gray-700">{row.label}</span>
            </div>
            <span className="min-w-0 truncate text-right text-base font-black tabular-nums text-gray-950">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
